import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import type { Observable } from 'rxjs';
import { fromEvent, merge, of, map, iif } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { filter, take, catchError, switchMap } from 'rxjs/operators';
import { TT_ASSETS_ENDPOINT } from '../di-tokens';
import type { HttpResponse } from '@angular/common/http';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class OnlineService {
  isOnline$: Observable<boolean>;
  isDefinitelyOnline$: Observable<boolean>;
  private _isOnline: boolean = null;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId,
    @Inject(TT_ASSETS_ENDPOINT) private s3Endpoint
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // https://gist.github.com/ccnokes/bd5b975dab213bb60b877e49754dd675?permalink_comment_id=3192446#gistcomment-3192446
      this.isOnline$ = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline')).pipe(
        map(() => navigator.onLine)
      );

      this.isDefinitelyOnline$ = this.isOnline$.pipe(
        filter((isOnline: boolean): boolean => isOnline !== null),
        switchMap((isOnline: boolean) =>
          iif(
            () => isOnline,
            this.http
              .head(`${this.s3Endpoint}/ping.json`, {
                observe: 'response',
                headers: new HttpHeaders({
                  'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
                }),
              })
              .pipe(
                catchError((err) => of(err)),
                map((res: HttpResponse<Object>) => res.status >= 200 && res.status < 300)
              ),
            of(false)
          )
        ),
        take(1)
      );

      this.isOnline$.subscribe((isOnline: boolean) => (this._isOnline = isOnline));
    } else {
      this.isOnline$ = of(true);
      this.isDefinitelyOnline$ = of(true);
    }
  }

  public get isOnline(): boolean {
    return this._isOnline;
  }
}
